import { effect, inject, Injectable, Injector } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Dialog } from '@capacitor/dialog';
import { filter } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { EnvironmentService } from './environment.service';

import { App } from '@capacitor/app';
import { NativeMarket } from '@capgo/capacitor-native-market';
import {
  isFrontAppVersionUpToDate,
  LastDeployedVersions,
} from '../../shared/models/schemas/front-app-version.schema';
import { GoogleAnalyticsService } from './google-analytics.service';
import { IsApplicationService } from './is-application.service';
import { LastDeployedVersionsService } from './last-deployed-versions.service';

declare const cordova: any;
@Injectable({
  providedIn: 'root',
})
export class FrontAppVersionAppService {
  private injector = inject(Injector);
  private router = inject(Router);
  private environmentService = inject(EnvironmentService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private isApplicationService = inject(IsApplicationService);
  private lastDeployedVersionsService = inject(LastDeployedVersionsService);

  newMobileVersionAvailable = false;
  newBreakingVersionAvailable = false;
  newMobileVersionAvailableDismissed = false;

  init() {
    if (this.isApplicationService.isApplication() && environment.production) {
      effect(
        () => {
          const lastDeployedVersions = this.lastDeployedVersionsService.lastDeployedVersions();
          this.checkNewVersionAvailable(lastDeployedVersions);
        },
        { injector: this.injector }
      );

      // affichage des popups seulement aux navigations pour éviter à l'utilisateur de le stopper au milieu de quelque chose s'il y a une nouvelle version
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
        if (this.newBreakingVersionAvailable) {
          this.openVersionUpdateForce();
        } else if (this.newMobileVersionAvailable && !this.newMobileVersionAvailableDismissed) {
          this.openVersionUpdateWarn();
          this.newMobileVersionAvailableDismissed = true;
        }
      });

      // gère le cas où l'utilisateur revient du store sans avoir mis à jour l'app
      App.addListener('resume', () => {
        if (this.newBreakingVersionAvailable) {
          this.openVersionUpdateForce();
        }
      });
    }
  }

  checkNewVersionAvailable(lastDeployedVersions: LastDeployedVersions | null) {
    if (!lastDeployedVersions) return;

    // si on est moins avancé que la dernière version breaking ET
    // qu'il existe une version mobile plus avancée ou équivalente à la dernière version breaking
    if (
      lastDeployedVersions.lastBreakingVersion &&
      lastDeployedVersions.lastMobileVersion &&
      !isFrontAppVersionUpToDate(
        this.environmentService.frontAppVersion,
        lastDeployedVersions.lastBreakingVersion
      ) &&
      (!isFrontAppVersionUpToDate(
        lastDeployedVersions.lastBreakingVersion,
        lastDeployedVersions.lastMobileVersion
      ) ||
        lastDeployedVersions.lastBreakingVersion.name ===
          lastDeployedVersions.lastMobileVersion.name)
    ) {
      this.newBreakingVersionAvailable = true;
    }
    // si on est moins avancé que la dernière version mobile
    else if (
      lastDeployedVersions.lastMobileVersion &&
      !isFrontAppVersionUpToDate(
        this.environmentService.frontAppVersion,
        lastDeployedVersions.lastMobileVersion
      )
    ) {
      this.newMobileVersionAvailable = true;
    }
  }

  openVersionUpdateWarn() {
    Dialog.confirm({
      title: $localize`:@@version.nouvelle_version:Nouvelle version !`,
      message: `${$localize`:@@version.nouvelle_version_disponible:Une nouvelle version de l'application est disponible !`} (${
        this.lastDeployedVersionsService.lastDeployedVersions()?.lastVersion.name
      } -> ${this.environmentService.stringifiedFrontAppVersion})`,
      okButtonTitle: $localize`:@@version.telecharger:Télécharger`,
      cancelButtonTitle: $localize`:@@utils.plus_tard:Plus tard`,
    }).then((confirmResult) => {
      if (confirmResult.value) {
        this.openMarket(false);
      } else {
        this.googleAnalyticsService.sendEvent(
          'refuse update app',
          'update',
          'from ' +
            this.environmentService.stringifiedFrontAppVersion +
            ' to ' +
            this.lastDeployedVersionsService.lastDeployedVersions()?.lastVersion.name
        );
      }
    });
  }

  openVersionUpdateForce() {
    Dialog.alert({
      title: $localize`:@@version.nouvelle_version:Nouvelle version !`,
      message: `${$localize`:@@version.nouvelle_version_disponible:Une nouvelle version de l'application est disponible !`} ${$localize`:@@version.telecharger_obligatoire:Tu dois faire la mise à jour pour continuer à l'utiliser.`} (${
        this.lastDeployedVersionsService.lastDeployedVersions()?.lastVersion.name
      } -> ${this.environmentService.stringifiedFrontAppVersion})`,
      buttonTitle: $localize`:@@version.telecharger:Télécharger`,
    }).then(() => {
      this.openMarket(true);
    });
  }

  async openMarket(force: boolean) {
    const appInfo = await App.getInfo();

    this.googleAnalyticsService.sendEvent(
      'accept update app',
      'update',
      'from ' +
        this.environmentService.stringifiedFrontAppVersion +
        ' to ' +
        this.lastDeployedVersionsService.lastDeployedVersions()?.lastVersion.name
    );

    await NativeMarket.openStoreListing({
      appId: appInfo.id,
      country: environment.defaultLanguage.toUpperCase(),
    });

    if (force) {
      this.openVersionUpdateForce();
    }
  }
}
